import type { AtmospherePreset } from '../presets.config';
import { ATMOSPHERE_PALETTE as P } from '../palette';
import { Z_LAYERS } from '../../../game/rendering/Z_LAYERS';
import { breathingGlowBlobs, multiPlaneParallax } from '../presetBuilders';
import { WOODLAND_BACKDROPS } from '../../../game/effects/atmosphere/silhouettes/woodlandBackdrops';

/** Cozy forest — lobby / town / social. Fireflies, warm god-rays, floor + canopy glow, fog wisps,
 *  + a near foreground foliage occluder (Phase 1.1) for immediate-foreground depth. */
export const COZY_FOREST: AtmospherePreset = {
  id: 'cozy_forest',
  layers: [
    { kind: 'skyGradient', from: 0x112b2c, to: 0x789482 },
    ...multiPlaneParallax([
      { shape: 'ridge', color: 0x45675e, speed: 0.025, height: 430, alpha: 0.7, svg: WOODLAND_BACKDROPS.forest.far },
      { shape: 'ridge', color: 0x284d3c, speed: 0.07, height: 340, alpha: 0.84, svg: WOODLAND_BACKDROPS.forest.mid },
      { shape: 'ridge', color: 0x17342c, speed: 0.14, height: 190, alpha: 0.92, svg: WOODLAND_BACKDROPS.forest.near },
    ]),
    {
      kind: 'fireflies', varyOnReload: true, // the lobby re-arranges its fireflies each visit
      count: 28, colors: [P.firefly, P.mint, P.softPink],
      sizeMin: 14, sizeMax: 28, driftPx: 30,
      periodMinMs: 7000, periodMaxMs: 14000, alphaMin: 0.4, alphaMax: 1.0,
    },
    {
      kind: 'godRays',
      count: 5, color: P.amber, widthMin: 70, widthMax: 160, height: 520,
      tiltDeg: 16, periodMinMs: 9000, periodMaxMs: 17000, alphaMin: 0.2, alphaMax: 0.55,
    },
    breathingGlowBlobs(
      [
        { color: P.mint, atX: 0.5, atY: 0.95, size: 520, alpha: 0.1 }, // forest-floor glow
        { color: P.amber, atX: 0.5, atY: 0.05, size: 560, alpha: 0.1 }, // canopy bloom
      ],
      // Dappled canopy light shifts gently.
      { periodMs: 8000, min: 0.8, max: 1.2 },
    ),
    {
      kind: 'fog', color: P.mint, bands: 4,
      alphaMin: 0.03, alphaMax: 0.1, driftPx: 60, periodMinMs: 14000, periodMaxMs: 24000,
    },
    // Ambient life — a few soft motes drifting across the canopy (distant birds).
    {
      kind: 'critters', count: 5, palette: [P.leaf, P.mint, P.firefly],
      sizeMin: 5, sizeMax: 9, speedMin: 22, speedMax: 40,
      bandYMin: 0.08, bandYMax: 0.32, direction: 'both',
    },
    // Foreground foliage occluder (Phase 1.1) — dark near silhouette anchored to
    // the GROUND, tips poking ~18px above it, scrolling FASTER than the camera
    // (speed 1.08) for immediate-foreground depth. Bottom-strip only + rendered
    // below name tags / damage text, so it frames the scene without hiding play.
    {
      kind: 'parallax', plane: 'foreground', shape: 'spires',
      color: 0x0b1810, speed: 1.08, y: 18, height: 110, alpha: 0.42,
      depth: Z_LAYERS.PARALLAX_FOREGROUND, detail: 1.6, sway: { ampPx: 3, periodMs: 26000 },
    },
    // Un-graded: preserves clean playability without an atmospheric rectangle overlay.
  ],
};
